import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalService,
} from '@azure/msal-angular';
import {
  LogLevel,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { environment } from '@ers-cat-app/env/environment';
import { Subject, filter, retry, takeUntil, timeout } from 'rxjs';
import { Claim } from '../interfaces/claim.interface';
import { AuthService, LoginResponse } from '../auth.service';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

@Injectable({
  providedIn: 'root',
})
export class MsalAuthService implements OnDestroy {
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  readonly msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  loginDisplay = false;
  dataSource: Claim[] = [];
  private readonly BASE_URL = `${environment.apiUrl}/auth`;
  private readonly _destroying$ = new Subject<void>();
  isAttemptingLogin = false;
  private loginAttemptId = 0;

  constructor() {
    this.init();
  }

  init(): void {
    this.checkAndSetActiveAccount();

    this.msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) =>
            status === InteractionStatus.None ||
            status === InteractionStatus.HandleRedirect,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS,
        ),
      )
      .subscribe(result => {
        const payload = result.payload as AuthenticationResult;
        if (payload?.account) {
          this.msalService.instance.setActiveAccount(payload.account);
        }
        // Only mint an app JWT after the user actually signed in as this
        // account. Do not reuse a leftover cached Azure account.
        this.syncStoredSessionWithActiveAccount({ allowAzureLogin: true });
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  postLoginWithAzure() {
    const attemptId = ++this.loginAttemptId;
    this.isAttemptingLogin = true;

    return this.http
      .post<LoginResponse>(`${this.BASE_URL}/loginWithAzure`, {})
      .pipe(
        timeout(10000), // Limit time to 10sec
        retry(3), // Maximum 3 retries
      )
      .subscribe({
        next: (response: LoginResponse) => {
          if (attemptId !== this.loginAttemptId) {
            return;
          }
          this.authService.onLoginSuccess(response, true);
          this.isAttemptingLogin = false;
        },
        error: () => {
          if (attemptId !== this.loginAttemptId) {
            return;
          }
          this.isAttemptingLogin = false;
        },
      });
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();

    // Never guess among multiple cached Azure accounts — that is how the
    // previous user can become the active session.
    if (!activeAccount && accounts.length === 1) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    this.setLoginDisplay();
    // Restore from the app JWT on refresh. Do not silently log in as a
    // leftover Azure account — that shows the wrong user.
    this.syncStoredSessionWithActiveAccount({ allowAzureLogin: false });
  }

  private syncStoredSessionWithActiveAccount(options: {
    allowAzureLogin: boolean;
  }) {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const hasAppJwt = this.authService.isAppAccessToken();
    const isAzureJwt = this.authService.isAzureUser();

    // A password login must not be replaced by a leftover Azure account.
    if (hasAppJwt && !isAzureJwt) {
      return;
    }

    const jwtEmail = this.authService.getEmailFromToken();
    const azureEmail =
      activeAccount?.username ||
      (activeAccount?.idTokenClaims?.preferred_username as string) ||
      '';
    const accountMismatch =
      hasAppJwt &&
      isAzureJwt &&
      !!jwtEmail &&
      !!azureEmail &&
      jwtEmail.toLowerCase() !== azureEmail.toLowerCase();

    if (accountMismatch) {
      this.authService.clearAccessToken();
    }

    if (
      options.allowAzureLogin &&
      activeAccount &&
      (!this.authService.isLoggedIn$.value || accountMismatch || !hasAppJwt)
    ) {
      this.postLoginWithAzure();
    }
  }

  async loginRedirect(loginHint?: string) {
    this.msalService.instance.setActiveAccount(null);
    try {
      await this.msalService.instance.clearCache();
    } catch {
      // Ignore cache-clear failures; select_account still lets the user pick.
    }

    const request: RedirectRequest = {
      ...((this.msalGuardConfig.authRequest || {}) as RedirectRequest),
      prompt: 'select_account',
      ...(loginHint ? { loginHint } : {}),
    };

    this.msalService.loginRedirect(request);
  }

  async logout(popup?: boolean) {
    if (popup) {
      await this.msalService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      // this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  isLoggedIn() {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  getClaims(claims: any) {
    const list: Claim[] = new Array<Claim>();

    Object.keys(claims).forEach(function (k, v) {
      const c: Claim = { id: 0, claim: '', value: '' };
      c.id = v;
      c.claim = k;
      c.value = claims ? claims[k] : null;
      list.push(c);
    });
    this.dataSource = list;
  }
}
